// ---------------------------------------------------------
// Datei: DocumentChecklist.tsx
// ---------------------------------------------------------
import { getEnv } from "../utils/env";
import React, { useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useDocumentStore } from "../store/documentState";
import { DocumentType } from "../types/documents";
import { getSignedUrl } from "../services/hubspotBackendClient";
import { deleteDriveFile } from "../services/api.extensions";
import FormularModal from "./FormularModal";
import DocumentPreviewModal from "./DocumentPreviewModal";
import { DealContact } from "../types/hubspot";
import { useQueue } from "../contexts/QueueContext";

interface DocumentChecklistProps {
  phaseId: string;
  onSelectDocument?: (document: DocumentType) => void;
  onError?: (error: string) => void;
  disabled?: boolean;
  dealContact?: DealContact;
}

const DocumentChecklist: React.FC<DocumentChecklistProps> = ({
  phaseId,
  onSelectDocument,
  onError,
  disabled = false,
  dealContact,
}) => {
  const {
    fetchDocuments,
    isPhaseLoading,
    getPhaseError,
    updateDocumentStatus,
    deleteDocument,
    getDocumentData,
    getDocumentFileId,
    saveDocumentFileId,
    saveDocumentData // WICHTIG: Um die URL lokal zu speichern
  } = useDocumentStore();

  // WICHTIG: useShallow für reaktives Update bei nested Array-Änderungen
  // Zustand v5: useShallow vergleicht Array-Inhalte statt nur Referenzen
  const documents = useDocumentStore(
    useShallow((state) => state.documents[phaseId] || [])
  ) as DocumentType[];
  const loading = isPhaseLoading(phaseId);
  const error = getPhaseError(phaseId);
  const { items: queueItems } = useQueue();

  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const [formularModalOpen, setFormularModalOpen] = useState(false);
  const [selectedFormular, setSelectedFormular] = useState<DocumentType | null>(null);
  
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<DocumentType | null>(null);
  const [previewData, setPreviewData] = useState<string>("");
  const [serialPreviewText, setSerialPreviewText] = useState<string | null>(null);
  const [serialPreviewTitle, setSerialPreviewTitle] = useState<string>('');

  const isFetchingRef = useRef(false);

  // === Load Documents ===
  useEffect(() => {
    if (!phaseId) { if (onError) onError("Phasen-ID fehlt"); return; }
    if ((documents && documents.length > 0) || loading || isFetchingRef.current) return;
    const isHubSpotPhase = phaseId.includes("-");
    if (isHubSpotPhase) return;
    isFetchingRef.current = true;
    fetchDocuments(phaseId).catch((err) => onError && onError(err.message)).finally(() => { isFetchingRef.current = false; });
  }, [phaseId, loading, documents, fetchDocuments]);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = () => setActionMenuOpen(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // === NEUER HELPER: URL FETCHEN & ERSETZEN ===
  const fetchAndCacheHubSpotUrl = async (fileId: string, docId: string) => {
    try {
      console.log(`[HELPER] Hole URL für FileID ${fileId}...`);
      
      // 1. GET Call an Backend (wie dein Postman Call)
      const signedUrl = await getSignedUrl(fileId);
      
      if (signedUrl) {
        console.log(`[HELPER] Neue URL erhalten. Aktualisiere Anzeige.`);
        
        // 2. URL im lokalen Store speichern (überschreibt alte Daten/Bilder)
        saveDocumentData(phaseId, docId, signedUrl);
        return signedUrl;
      }
    } catch (e) {
      console.error("[HELPER] Fehler beim Abrufen der URL:", e);
    }
    return null;
  };

  // === Handlers ===

  const openDrivePreview = async (driveUrl: string, document: DocumentType) => {
    const driveFileId = driveUrl.split('/d/')[1]?.split('/')[0];
    if (!driveFileId) return;
    try {
      const authBase = import.meta.env.DEV ? '/auth-api' : (getEnv('VITE_AUTH_API_URL') || '/auth-api');
      const res = await fetch(`${authBase}/google/drive/file/${driveFileId}`, { credentials: 'include' });
      const json = await res.json();
      if (json.status && json.base64Content) {
        setPreviewDocument(document);
        setPreviewData(`data:${json.mimeType};base64,${json.base64Content}`);
        setPreviewModalOpen(true);
      }
    } catch (e) {
      console.error('[Preview] Drive fetch fehlgeschlagen', e);
    }
  };

  const handleDocumentSelect = async (document: DocumentType) => {
    if (document._id === 'seriennummer-ausseneinheit' || document._id === 'seriennummer-registrieren') {
      const textKey = document._id === 'seriennummer-ausseneinheit'
        ? 'seriennummer-ausseneinheit-text'
        : 'seriennummer-registrieren-text';
      const serialNumber = getDocumentData(phaseId, textKey)
        || ((document as any).fileId as string | undefined);
      // seriennummer-registrieren: Preview nur bei completed (Webhook-Bestätigung).
      // Bei pending immer das Eingabe-Modal öffnen.
      const canPreview = document._id !== 'seriennummer-registrieren' || document.status === 'completed';
      if (serialNumber && canPreview) {
        setSerialPreviewTitle(document.name);
        setSerialPreviewText(serialNumber);
      } else if (onSelectDocument) {
        onSelectDocument(document);
      }
      return;
    }

    // Wenn wir schon eine URL im Store haben (durch den Helper oben), nimm die!
    const localData = getDocumentData(phaseId, document._id);
    if (localData && localData.startsWith('https://drive.google.com/file/d/')) {
        await openDrivePreview(localData, document);
        return;
    }
    if (localData && localData.startsWith('http')) {
        setPreviewDocument(document);
        setPreviewData(localData);
        setPreviewModalOpen(true);
        return;
    }

    const hubspotDoc = document as any;
    if (hubspotDoc.fileId && hubspotDoc.status === "completed") {
      if (hubspotDoc.fileId.startsWith('https://drive.google.com/')) {
        await openDrivePreview(hubspotDoc.fileId, document);
      } else {
        handleOpenHubSpotDocument(hubspotDoc.fileId, document);
      }
      return;
    }
    if (document.status === "completed") {
      const storedFileId = getDocumentFileId(phaseId, document._id);
      if (storedFileId) {
        handleOpenHubSpotDocument(storedFileId, document);
      } else {
        handleOpenLocalDocumentPreview(document);
      }
      return;
    }
    if (hasFormularTemplate(document)) {
      handleOpenFormular(undefined, document);
    } else {
      if (onSelectDocument) onSelectDocument(document);
    }
  };

  const handleOpenLocalDocumentPreview = async (document: DocumentType) => {
    let data = getDocumentData(phaseId, document._id);
    if (!data) {
        const qItem = queueItems.find((i: any) => i.data?.documentId === document._id);
        if(qItem?.data?.imageData) data = qItem.data.imageData;
    }
    if(!data) {
      if (onSelectDocument) onSelectDocument(document);
      return;
    }
    setPreviewDocument(document);
    setPreviewData(data);
    setPreviewModalOpen(true);
  };

  const handleOpenHubSpotDocument = async (fileId: string, document: DocumentType) => {
    try {
      const url = await getSignedUrl(fileId);
      if(!url) throw new Error("No URL");
      setPreviewDocument(document);
      setPreviewData(url);
      setPreviewModalOpen(true);
    } catch(e) { console.error(e); alert("Fehler beim Laden."); }
  };

  const getDriveFileId = (docId: string): string | null => {
    const url = getDocumentData(phaseId, docId);
    if (!url?.startsWith('https://drive.google.com/file/d/')) return null;
    return url.split('/d/')[1]?.split('/')[0] ?? null;
  };

  const handleDeleteDocument = async (e: React.MouseEvent, docId: string) => {
    e.stopPropagation();
    if(window.confirm("Löschen?")) {
      const driveFileId = getDriveFileId(docId);
      if (driveFileId) {
        try { await deleteDriveFile(driveFileId); } catch (err) { console.error('[Delete] Drive-Fehler:', err); }
      }
      await deleteDocument(phaseId, docId);
      setActionMenuOpen(null);
    }
  };

  const handleReplaceDocument = async (e: React.MouseEvent | undefined, document: DocumentType) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    setActionMenuOpen(null);
    // const driveFileId = getDriveFileId(document._id);
    // if (driveFileId) {
    //   try { await deleteDriveFile(driveFileId); } catch (err) { console.error('[Replace] Drive-Fehler:', err); }
    // }
    if (hasFormularTemplate(document)) {
      handleOpenFormular(undefined, document);
    } else {
      if (onSelectDocument) onSelectDocument(document);
    }
  };

  const toggleActionMenu = (e: React.MouseEvent, docId: string) => {
    e.stopPropagation();
    setActionMenuOpen(actionMenuOpen === docId ? null : docId);
  };

  const handleOpenFormular = (e: React.MouseEvent | undefined, document: DocumentType) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    setSelectedFormular(document);
    setFormularModalOpen(true);
  };

  // === Formular Logic ===
  const hasFormularTemplate = (doc: DocumentType) => {
    const ids = ["inbetriebnahmeprotokoll", "vdz-formular", "hydraulischer-abgleich"];
    return ids.includes(doc._id) || doc.name.toLowerCase().includes("inbetriebnahm") || doc.name.toLowerCase().includes("vdz");
  };

  const getTemplatePath = (docId: string) => {
    if(docId.includes("inbetriebnahm")) return "/Templates/Inbetriebnahmeprotokoll.html";
    return "/Templates/VdZ_Formular.html"; 
  };

  // UPDATE: Webhook Success Handler
  const handleFormularUploadSuccess = async (fileId?: string) => {
    if (!selectedFormular) return;
    
    console.log(`[CHECKLIST] Upload Success. ID: ${fileId}`);
    
    if (fileId) {
        // 1. ID speichern (setzt auch status auf "completed" im Store)
        saveDocumentFileId(phaseId, selectedFormular._id, fileId);
        
        // 2. AWAIT: Helper ruft URL ab und speichert sie
        // Damit wird das "alte" Bild sofort durch das PDF ersetzt
        await fetchAndCacheHubSpotUrl(fileId, selectedFormular._id);
    } else {
        // Fallback: Nur Status setzen wenn keine fileId vorhanden
        updateDocumentStatus(phaseId, selectedFormular._id, "completed");
    }
  };

  // === RENDER ===
  const getStatusBadge = (req: boolean) => req ? 
    <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Pflicht</span> : 
    <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Optional</span>;

  return (
    <div className="pb-24" data-testid="document-checklist">
      {loading && <div className="p-4 text-center">Lade...</div>}
      {error && <div className="p-4 text-red-500">{error}</div>}

      {!loading && !error && documents.map((doc) => {
        const canOpenExistingDocument = doc.status === 'completed';

        return (
        <div key={doc._id}
             data-testid="document-item"
             data-testid-card="document-card"
             onClick={() => (!disabled || canOpenExistingDocument) && handleDocumentSelect(doc)}
             className={`w-full bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4 transition-all ${doc.status === 'completed' ? 'border-l-4 border-green-500' : ''}`}>
          
          <div className="flex justify-between mb-2">
            <div>
                <h3 className="font-bold text-gray-900 dark:text-white">{doc.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{doc.description}</p>
            </div>
            <div className="flex flex-col items-end gap-1">
                {getStatusBadge(doc.required)}
            </div>
          </div>

          <div className="flex justify-between items-center mt-3">
            {doc.status === 'completed' ? (
                <div className="text-green-600 text-sm flex items-center">
                    <span className="mr-1">✓</span> 
                    {/* Zeige "Anzeigen", wenn wir eine ID oder URL haben */}
                    {(doc as any).fileId || getDocumentFileId(phaseId, doc._id) || !!getDocumentData(phaseId, doc._id) ? "Anzeigen" : "Wird verarbeitet..."}
                </div>
            ) : (
                <div className="text-blue-600 text-sm">Dokument hinzufügen</div>
            )}

            {doc.status === 'completed' && (
                <div className="relative">
                    <button
                      type="button"
                      onClick={(e) => toggleActionMenu(e, doc._id)}
                      className="p-2 text-gray-500 dark:text-gray-400"
                      aria-label={`${doc.name} Aktionen`}
                      aria-expanded={actionMenuOpen === doc._id}
                    >
                      •••
                    </button>
                    {actionMenuOpen === doc._id && (
                        <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-slate-700 shadow-xl border border-gray-200 dark:border-slate-600 rounded z-50">
                            <button onClick={(e) => { e.stopPropagation(); handleDocumentSelect(doc); }} className="block w-full text-left px-4 py-2 text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-slate-600">Anzeigen</button>
                            <button onClick={(e) => handleReplaceDocument(e, doc)} className="block w-full text-left px-4 py-2 text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-slate-600">Ersetzen</button>
                            <button onClick={(e) => handleDeleteDocument(e, doc._id)} className="block w-full text-left px-4 py-2 text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-slate-600">Löschen</button>
                        </div>
                    )}
                </div>
            )}
          </div>
        </div>
        );
      })}

      {formularModalOpen && selectedFormular && (
        <FormularModal
          isOpen={formularModalOpen}
          onClose={() => { setFormularModalOpen(false); setSelectedFormular(null); }}
          templatePath={getTemplatePath(selectedFormular._id)}
          documentTitle={selectedFormular.name}
          dealId={phaseId.split('-')[0]}
          phaseId={phaseId}
          documentId={selectedFormular._id}
          onUploadComplete={handleFormularUploadSuccess}
          dealContact={dealContact}
        />
      )}

      {serialPreviewText !== null && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 px-0 sm:px-4" onClick={() => setSerialPreviewText(null)}>
          <div className="w-full sm:max-w-sm bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl px-6 py-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{serialPreviewTitle}</h2>
              <button onClick={() => setSerialPreviewText(null)} className="text-gray-500 hover:text-gray-800 dark:hover:text-white min-w-[44px] min-h-[44px] flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p
              className="font-mono font-bold text-gray-900 dark:text-white text-center py-4 bg-gray-50 dark:bg-gray-700 rounded-xl whitespace-nowrap overflow-hidden"
              style={{ fontSize: `clamp(0.875rem, ${Math.min(2, 35 / Math.max(serialPreviewText.length, 1))}rem, 2rem)` }}
            >
              {serialPreviewText}
            </p>
          </div>
        </div>
      )}

      {previewModalOpen && previewDocument && (
        <DocumentPreviewModal
          isOpen={previewModalOpen}
          onClose={() => { setPreviewModalOpen(false); setPreviewDocument(null); }}
          documentName={previewDocument.name}
          documentType={previewData.startsWith("data:") ? "image" : "pdf"}
          previewData={previewData}
          onReplace={() => handleReplaceDocument(undefined, previewDocument)}
        />
      )}
    </div>
  );
};

export default DocumentChecklist;
